// A TUNE MFD as the owner manages it from the web: when it was last seen and what it runs, putting it in a car (or
// moving it to another), and signing it out.
import { useMutation } from '@tanstack/react-query';
import type { components } from '@tune/shared';
import { useId, useState, type FormEvent } from 'react';
import { ApiError, data, problem } from '../../api';
import { useAuth } from '../../auth';
import { describeCar, seenThisWeek } from '../../lib/car';
import { useIdempotencyKey } from '../../lib/idempotency';
import { agoText, dayText, deviceName } from '../../lib/words';
import { useCarsChanged, useVehicles } from '../../queries';
import {
  Banner,
  Button,
  Card,
  Dot,
  ListChoice,
  ListSection,
  Sheet,
  TextField,
  useConfirm,
  useNow,
  useRouteSheet,
  useToast,
} from '../../ui';
import { ArrowsLeftRightIcon, SignOutIcon } from '../../ui/icons';
import s from './Garage.module.css';

type Device = components['schemas']['Device'];
type Vehicle = components['schemas']['Vehicle'];

/** When a device was last seen, in words, with a dot that's green for this week. */
export function SeenLine({ device }: { device: Pick<Device, 'lastSeenAt'> }) {
  const now = useNow(60_000);
  return (
    <span className={s.seen}>
      <Dot tone={seenThisWeek(device.lastSeenAt, now) ? 'good' : 'neutral'} />
      {device.lastSeenAt ? `Seen ${agoText(device.lastSeenAt, now)}` : 'Not seen yet'}
    </span>
  );
}

/** Signing a device out, after asking: it stops syncing until it's signed in again with a code. */
export function useSignOutDevice(device: Device) {
  const api = useAuth().api('owners');
  const changed = useCarsChanged();
  const confirm = useConfirm();
  const say = useToast();
  const name = deviceName(device);
  const mfd = device.kind === 'mfd';
  const signOut = useMutation({
    mutationFn: () => data(api.POST('/devices/{deviceId}/sign-out', { params: { path: { deviceId: device.id } } })),
    onSuccess: async () => {
      await changed();
      say(`${mfd ? 'The MFD' : name} is signed out.`);
    },
    onError: async (err) => {
      // Signed out already, elsewhere: the page catches up.
      if (err instanceof ApiError && err.status === 404) {
        await changed();
      }
    },
  });
  const ask = async () => {
    const yes = await confirm({
      title: mfd ? 'Sign out the TUNE MFD?' : `Sign out ${name}?`,
      message: mfd
        ? "It stops syncing until it's signed in again, with the code on its TUNE account page."
        : "It stops syncing until it's signed in again.",
      confirm: 'Sign out',
    });
    if (yes) {
      signOut.mutate();
    }
  };
  return { ask, pending: signOut.isPending, error: signOut.error };
}

/**
 * A TUNE MFD: when it was seen, when it signed in and what TUNE Live it runs, with Move (or, in no car, Put it in a
 * car) and Sign out. `sheet` names the address's sheet for choosing its car.
 */
export function MfdCard({ device, car, sheet }: { device: Device; car: Vehicle | null; sheet: string }) {
  const [choosing, setChoosing] = useRouteSheet(sheet);
  const signOut = useSignOutDevice(device);
  const failed = signOut.error && !(signOut.error instanceof ApiError && signOut.error.status === 404);
  return (
    <Card
      title={deviceName(device)}
      subtitle={
        <span className={s.lines}>
          <SeenLine device={device} />
          <span>
            Signed in {dayText(device.signedInAt)}
            {device.appVersion !== null && ` · TUNE Live ${device.appVersion}`}
          </span>
        </span>
      }
      level={car ? 2 : 3}
    >
      {failed && <Banner tone="error">{problem(signOut.error)}</Banner>}
      <div className={s.actions}>
        <Button
          variant="gray"
          size="small"
          icon={<ArrowsLeftRightIcon size={16} weight="bold" />}
          onClick={() => setChoosing(true)}
        >
          {car ? 'Move to another car' : 'Put it in a car'}
        </Button>
        <Button
          variant="destructivePlain"
          size="small"
          icon={<SignOutIcon size={16} weight="bold" />}
          busy={signOut.pending}
          onClick={() => void signOut.ask()}
        >
          Sign out
        </Button>
      </div>
      <ChooseCarSheet device={device} from={car} open={choosing} onClose={() => setChoosing(false)} />
    </Card>
  );
}

/** Where an MFD goes: one of the owner's cars, or a new one by its name. */
type Choice = { vehicleId: string; nickname: string } | { newCar: string };

/** Which car an MFD is in: one of the owner's others, or a new one. */
export function ChooseCarSheet({
  device,
  from,
  open,
  onClose,
}: {
  device: Device;
  from: Vehicle | null;
  open: boolean;
  onClose: () => void;
}) {
  const id = useId();
  const api = useAuth().api('owners');
  const changed = useCarsChanged();
  const say = useToast();
  const idempotency = useIdempotencyKey();
  const move = useMutation({
    mutationFn: async (choice: Choice) => {
      let to = choice;
      if ('newCar' in to) {
        const body = { nickname: to.newCar };
        const made = await data(
          api.POST('/vehicles', { params: { header: { 'Idempotency-Key': idempotency.keyFor(body) } }, body }),
        );
        to = { vehicleId: made.id, nickname: made.nickname };
      }
      await data(
        api.PUT('/devices/{deviceId}/vehicle', {
          params: { path: { deviceId: device.id } },
          body: { vehicleId: to.vehicleId },
        }),
      );
      return to.nickname;
    },
    onSuccess: async (nickname) => {
      // Only now: tried again after the move failed, the same new car is found rather than another made.
      idempotency.made();
      onClose();
      await changed();
      say(`The MFD is in ${nickname} now.`);
    },
  });
  return (
    <Sheet
      open={open}
      onClose={() => {
        move.reset();
        onClose();
      }}
      title={from ? 'Move the MFD' : 'Which car is it in?'}
      action={{ label: from ? 'Move' : 'Put it in', form: id, busy: move.isPending }}
    >
      <ChooseCarForm id={id} from={from} failed={move.error} onChoose={(choice) => move.mutate(choice)} />
    </Sheet>
  );
}

function ChooseCarForm({
  id,
  from,
  failed,
  onChoose,
}: {
  id: string;
  from: Vehicle | null;
  failed: unknown;
  onChoose: (choice: Choice) => void;
}) {
  const vehicles = useVehicles();
  const cars = (vehicles.data?.vehicles ?? []).filter((car) => car.id !== from?.id);
  const [chosen, setChosen] = useState<string>(cars.length === 0 ? 'new' : '');
  const [nickname, setNickname] = useState('');
  const [unchosen, setUnchosen] = useState(false);
  const [unnamed, setUnnamed] = useState(false);
  const group = useId();
  const target = cars.find((car) => car.id === chosen);
  const choose = (value: string) => {
    setChosen(value);
    setUnchosen(false);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (chosen === 'new') {
      if (nickname.trim()) {
        onChoose({ newCar: nickname.trim() });
      } else {
        setUnnamed(true);
      }
    } else if (target) {
      onChoose({ vehicleId: target.id, nickname: target.nickname });
    } else {
      setUnchosen(true);
    }
  };
  return (
    <form id={id} className={s.form} onSubmit={submit} noValidate>
      {failed !== null && failed !== undefined && <Banner tone="error">{movedProblem(failed)}</Banner>}
      {unchosen && <Banner tone="error">Choose the car it's in.</Banner>}
      <ListSection header={from ? `From ${from.nickname} to` : 'Your cars'} level={3}>
        {cars.map((car) => (
          <ListChoice
            key={car.id}
            name={group}
            value={car.id}
            checked={chosen === car.id}
            onChange={() => choose(car.id)}
            label={car.nickname}
            subtitle={car.mfd ? 'Has a TUNE MFD' : describeCar(car) || undefined}
          />
        ))}
        <ListChoice
          name={group}
          value="new"
          checked={chosen === 'new'}
          onChange={() => choose('new')}
          label="A new car"
        />
      </ListSection>
      {chosen === 'new' && (
        <TextField
          label="What do you call it?"
          hint='Like "R34". You can say more about it later.'
          value={nickname}
          onChange={(e) => {
            setNickname(e.target.value);
            setUnnamed(false);
          }}
          maxLength={40}
          autoComplete="off"
          error={unnamed ? 'Give the car a name.' : undefined}
        />
      )}
      <p className={s.note}>
        {target?.mfd
          ? `${target.nickname} has a TUNE MFD already. That one stays signed in, in no car, and asks which car it's in. `
          : ''}
        The MFD takes on the car's settings, connections and trips once the car is stopped.
      </p>
    </form>
  );
}

function movedProblem(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return 'That MFD or car has gone. Close this, and the page catches up.';
  }
  if (error instanceof ApiError && error.code === 'quota_exceeded') {
    return 'Your account has as many cars as it can. Choose one of them.';
  }
  return problem(error);
}
